import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, AlertTriangle, Activity, Target,
  CheckCircle, Clock, TrendingUp, TrendingDown, Loader2,
  Edit, X, BarChart3, User
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const levelConfig = {
  low: { bg: 'bg-green-100 text-green-700', ring: 'stroke-green-500', color: 'text-green-600', label: 'Low Risk' },
  medium: { bg: 'bg-yellow-100 text-yellow-700', ring: 'stroke-yellow-500', color: 'text-yellow-600', label: 'Medium Risk' },
  high: { bg: 'bg-orange-100 text-orange-700', ring: 'stroke-orange-500', color: 'text-orange-600', label: 'High Risk' },
  critical: { bg: 'bg-red-100 text-red-800', ring: 'stroke-red-500', color: 'text-red-600', label: 'Critical Risk' },
};
const statusConfig = {
  identified: { bg: 'bg-blue-100 text-blue-700', label: 'Identified', step: 0 },
  assessing: { bg: 'bg-yellow-100 text-yellow-700', label: 'Assessing', step: 1 },
  mitigating: { bg: 'bg-orange-100 text-orange-700', label: 'Mitigating', step: 2 },
  monitoring: { bg: 'bg-purple-100 text-purple-700', label: 'Monitoring', step: 3 },
  closed: { bg: 'bg-green-100 text-green-700', label: 'Closed', step: 4 },
};

const RiskDetailPage = () => {
  const { riskId } = useParams();
  const navigate = useNavigate();
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updateForm, setUpdateForm] = useState({});

  useEffect(() => {
    api.get(`/api/risks/${riskId}`)
      .then(setRisk)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [riskId]);

  const openUpdate = () => {
    setUpdateForm({
      status: risk.status || 'identified',
      level: risk.level || 'medium',
      likelihood: risk.likelihood || 3,
      impact: risk.impact || 3,
      mitigation_plan: risk.mitigation_plan || '',
      review_date: risk.review_date ? risk.review_date.split('T')[0] : '',
    });
    setShowUpdateModal(true);
  };

  const handleUpdate = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...updateForm,
        likelihood: parseInt(updateForm.likelihood),
        impact: parseInt(updateForm.impact),
      };
      const updated = await api.put(`/api/risks/${riskId}`, payload);
      setRisk(updated);
      toast.success('Risk updated');
      setShowUpdateModal(false);
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const quickStatusChange = async (newStatus) => {
    try {
      const updated = await api.put(`/api/risks/${riskId}`, { status: newStatus });
      setRisk(updated);
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div>;
  if (!risk) return <div className="text-center py-24 text-gray-400">Risk not found</div>;

  const lvl = levelConfig[risk.level] || levelConfig.medium;
  const stat = statusConfig[risk.status] || statusConfig.identified;
  const currentStep = stat.step;
  const riskScore = risk.risk_score || (risk.likelihood * risk.impact);

  return (
    <div className="space-y-6" data-testid="risk-detail-page">
      <button onClick={() => navigate('/hospital/risk-register')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm transition-colors" data-testid="back-to-risks">
        <ArrowLeft className="w-4 h-4" /> Back to Risk Register
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${lvl.bg}`}>{lvl.label}</span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${stat.bg}`}>{stat.label}</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{risk.category}</span>
            </div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900" data-testid="risk-title">{risk.title}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
              {risk.department && <span className="flex items-center gap-1"><Activity className="w-3.5 h-3.5" />{risk.department}</span>}
              {risk.owner_name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{risk.owner_name}</span>}
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{new Date(risk.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openUpdate} className="gap-1.5" data-testid="update-risk-btn">
              <Edit className="w-4 h-4" /> Update Assessment
            </Button>
            {risk.status !== 'closed' && risk.status !== 'monitoring' && (
              <Button size="sm" onClick={() => quickStatusChange(risk.status === 'identified' ? 'assessing' : risk.status === 'assessing' ? 'mitigating' : 'monitoring')}
                className="gap-1.5 btn-primary" data-testid="advance-risk-btn">
                <Target className="w-4 h-4" /> Advance Status
              </Button>
            )}
            {risk.status === 'monitoring' && (
              <Button size="sm" onClick={() => quickStatusChange('closed')} className="gap-1.5 bg-green-600 hover:bg-green-700 text-white" data-testid="close-risk-btn">
                <CheckCircle className="w-4 h-4" /> Close Risk
              </Button>
            )}
          </div>
        </div>

        {/* Risk Lifecycle Progress */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between relative">
            <div className="absolute top-5 left-4 right-4 h-0.5 bg-gray-200" />
            <div className="absolute top-5 left-4 h-0.5 bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] transition-all" style={{ width: `${(currentStep / 4) * (100 - 4)}%` }} />
            {[
              { label: 'Identified', step: 0, icon: AlertTriangle },
              { label: 'Assessing', step: 1, icon: BarChart3 },
              { label: 'Mitigating', step: 2, icon: Shield },
              { label: 'Monitoring', step: 3, icon: Activity },
              { label: 'Closed', step: 4, icon: CheckCircle },
            ].map(s => (
              <div key={s.step} className="flex flex-col items-center z-10 relative">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                  s.step <= currentStep ? 'bg-[var(--niko-teal)] border-[var(--niko-teal)] text-white' : 'bg-white border-gray-200 text-gray-400'
                }`}><s.icon className="w-4 h-4" /></div>
                <span className={`text-[10px] mt-1 font-medium ${s.step <= currentStep ? 'text-[var(--niko-teal)]' : 'text-gray-400'}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Risk Matrix */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="risk-matrix-card">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Risk Assessment Matrix</h2>
            <div className="grid grid-cols-3 gap-6">
              {/* Score Ring */}
              <div className="flex flex-col items-center">
                <div className="relative w-24 h-24">
                  <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className="stroke-gray-100" />
                    <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className={lvl.ring}
                      strokeDasharray={`${(riskScore / 25) * 264} 264`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-2xl font-bold ${lvl.color}`}>{riskScore}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Risk Score (out of 25)</p>
              </div>

              {/* Likelihood */}
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-2">Likelihood</p>
                <p className="text-3xl font-bold text-gray-900">{risk.likelihood}</p>
                <p className="text-xs text-gray-400">out of 5</p>
                <div className="flex gap-1 justify-center mt-2">
                  {[1,2,3,4,5].map(n => (
                    <div key={n} className={`w-4 h-4 rounded-sm ${n <= risk.likelihood ? 'bg-[var(--niko-teal)]' : 'bg-gray-100'}`} />
                  ))}
                </div>
              </div>

              {/* Impact */}
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-2">Impact</p>
                <p className="text-3xl font-bold text-gray-900">{risk.impact}</p>
                <p className="text-xs text-gray-400">out of 5</p>
                <div className="flex gap-1 justify-center mt-2">
                  {[1,2,3,4,5].map(n => (
                    <div key={n} className={`w-4 h-4 rounded-sm ${n <= risk.impact ? 'bg-orange-500' : 'bg-gray-100'}`} />
                  ))}
                </div>
              </div>
            </div>

            {/* 5x5 Heat Map */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2 text-center">Risk Heat Map (Likelihood vs Impact)</p>
              <div className="flex items-end gap-1 justify-center">
                <div className="text-[10px] text-gray-400 w-4 text-right pr-1 flex flex-col gap-1 pb-1">
                  {[5,4,3,2,1].map(l => <div key={l} className="h-6 flex items-center justify-end">{l}</div>)}
                </div>
                <div>
                  <div className="grid grid-cols-5 gap-1">
                    {[5,4,3,2,1].map(l => (
                      [1,2,3,4,5].map(i => {
                        const score = l * i;
                        const isCurrentCell = l === risk.likelihood && i === risk.impact;
                        const cellColor = score >= 15 ? 'bg-red-400' : score >= 10 ? 'bg-orange-300' : score >= 5 ? 'bg-yellow-300' : 'bg-green-300';
                        return (
                          <div key={`${l}-${i}`} className={`w-6 h-6 rounded-sm flex items-center justify-center text-[8px] font-bold ${cellColor} ${isCurrentCell ? 'ring-2 ring-gray-900 ring-offset-1' : 'opacity-50'}`}>
                            {isCurrentCell && score}
                          </div>
                        );
                      })
                    ))}
                  </div>
                  <div className="flex gap-1 mt-1">
                    {[1,2,3,4,5].map(i => <div key={i} className="w-6 text-center text-[10px] text-gray-400">{i}</div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Risk Description</h2>
            <p className="text-sm text-gray-600 leading-relaxed">{risk.description}</p>
          </div>

          {/* Mitigation Plan */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="mitigation-plan">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Mitigation Plan</h2>
            {risk.mitigation_plan ? (
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{risk.mitigation_plan}</p>
            ) : (
              <div className="text-center py-6">
                <Shield className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No mitigation plan recorded yet</p>
                <Button variant="outline" size="sm" onClick={openUpdate} className="mt-3 gap-1.5">
                  <Edit className="w-3.5 h-3.5" /> Add Mitigation Plan
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Risk Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between"><span className="text-gray-500">Category</span><span className="font-medium text-gray-900">{risk.category}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Level</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${lvl.bg}`}>{risk.level}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Status</span><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stat.bg}`}>{stat.label}</span></div>
              <div className="flex items-center justify-between"><span className="text-gray-500">Score</span><span className={`font-bold ${lvl.color}`}>{riskScore}/25</span></div>
              {risk.department && <div className="flex items-center justify-between"><span className="text-gray-500">Department</span><span className="font-medium text-gray-900">{risk.department}</span></div>}
              {risk.owner_name && <div className="flex items-center justify-between"><span className="text-gray-500">Owner</span><span className="font-medium text-gray-900">{risk.owner_name}</span></div>}
              {risk.review_date && <div className="flex items-center justify-between"><span className="text-gray-500">Next Review</span><span className="font-medium text-gray-900">{new Date(risk.review_date).toLocaleDateString()}</span></div>}
            </div>
          </div>

          {/* Risk Trend Indicator */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Risk Trend</h3>
            <div className="flex items-center gap-3">
              {risk.status === 'closed' ? (
                <><CheckCircle className="w-6 h-6 text-green-500" /><div><p className="font-medium text-green-600">Resolved</p><p className="text-xs text-gray-400">Risk has been closed</p></div></>
              ) : risk.status === 'monitoring' ? (
                <><Activity className="w-6 h-6 text-purple-500" /><div><p className="font-medium text-purple-600">Stable</p><p className="text-xs text-gray-400">Under active monitoring</p></div></>
              ) : risk.status === 'mitigating' ? (
                <><TrendingDown className="w-6 h-6 text-green-500" /><div><p className="font-medium text-green-600">Improving</p><p className="text-xs text-gray-400">Mitigation in progress</p></div></>
              ) : (
                <><TrendingUp className="w-6 h-6 text-red-500" /><div><p className="font-medium text-red-600">Active</p><p className="text-xs text-gray-400">Requires attention</p></div></>
              )}
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Activity Timeline</h3>
            <div className="relative">
              <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-100" />
              {[
                { label: 'Risk Identified', time: risk.created_at, color: 'bg-blue-500', done: true },
                ...(currentStep >= 1 ? [{ label: 'Assessment Started', time: risk.updated_at, color: 'bg-yellow-500', done: true }] : []),
                ...(currentStep >= 2 ? [{ label: 'Mitigation Active', time: risk.updated_at, color: 'bg-orange-500', done: true }] : []),
                ...(currentStep >= 3 ? [{ label: 'Under Monitoring', time: risk.updated_at, color: 'bg-purple-500', done: true }] : []),
                ...(currentStep >= 4 ? [{ label: 'Risk Closed', time: risk.updated_at, color: 'bg-green-500', done: true }] : []),
                ...(currentStep < 4 ? [{ label: 'Pending Resolution', time: null, color: 'bg-gray-300', done: false }] : []),
              ].map((entry, i) => (
                <div key={i} className="relative pl-8 pb-4 last:pb-0">
                  <div className={`absolute left-1.5 top-0.5 w-3 h-3 rounded-full ${entry.color} ring-2 ring-white`} />
                  <p className="text-sm font-medium text-gray-900">{entry.label}</p>
                  {entry.time && <p className="text-xs text-gray-400">{new Date(entry.time).toLocaleString()}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Update Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowUpdateModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="update-risk-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Update Risk Assessment</h2>
              <button onClick={() => setShowUpdateModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={updateForm.status} onChange={e => setUpdateForm({...updateForm, status: e.target.value})}>
                    {['identified', 'assessing', 'mitigating', 'monitoring', 'closed'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Level</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={updateForm.level} onChange={e => setUpdateForm({...updateForm, level: e.target.value})}>
                    {['low', 'medium', 'high', 'critical'].map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Likelihood (1-5)</Label>
                  <Input type="number" min="1" max="5" className="mt-1" value={updateForm.likelihood} onChange={e => setUpdateForm({...updateForm, likelihood: e.target.value})} data-testid="risk-likelihood-input" />
                  <p className="text-xs text-gray-400 mt-0.5">New Score: {updateForm.likelihood * updateForm.impact}</p>
                </div>
                <div>
                  <Label>Impact (1-5)</Label>
                  <Input type="number" min="1" max="5" className="mt-1" value={updateForm.impact} onChange={e => setUpdateForm({...updateForm, impact: e.target.value})} data-testid="risk-impact-input" />
                </div>
              </div>
              <div><Label>Mitigation Plan</Label><Textarea className="mt-1" rows={4} value={updateForm.mitigation_plan} onChange={e => setUpdateForm({...updateForm, mitigation_plan: e.target.value})} placeholder="Describe mitigation strategies..." data-testid="risk-mitigation-input" /></div>
              <div><Label>Next Review Date</Label><Input type="date" className="mt-1" value={updateForm.review_date} onChange={e => setUpdateForm({...updateForm, review_date: e.target.value})} /></div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowUpdateModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleUpdate} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-update-risk-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save Assessment
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskDetailPage;
